import { recalculateMeetRankings } from "../shared/util/src/methods";
import { resolveLiveReference } from "../shared/util/src/live-reference";
import { athletePowerscoutBests } from "@prisma-local/client";
import {
  AgeCategory,
  Discipline,
  Ligue,
  WeightCategory,
} from "@prisma-remote/client";
import { create } from "zustand";
import {
  applyCustomDisciplineLivePatch,
  type CustomDisciplineLivePatch,
} from "src/shared/util/src/custom-disciplines/live-patch";
import { getWebSocketUrl } from "../config";
import { apiClient } from "src/services/apiClient";
import {
  AthletePatch,
  AthleteWithAll,
  GroupPatch,
  GroupWithAll,
  MeetPatch,
  MeetWithAll,
  MeetWithLigue,
} from "../shared/util/src";

// Export des types pour utilisation externe
export type { MeetWithAll, MeetWithLigue };

// Types pour la création et mise à jour des meets
export interface CreateMeetData {
  isNational?: boolean;
  name: string;
  city: string;
  startdate: string;
  enddate: string;
  ligue_id: number;
}

export interface UpdateMeetData {
  isNational?: boolean;
  name?: string;
  city?: string;
  startdate?: string;
  enddate?: string;
  ligue_id?: number;
  isPublic?: boolean;
}

interface MeetState {
  patchCustomMeet: (patch: CustomDisciplineLivePatch) => void;
  meets: MeetWithLigue[];
  meetsWithAll: MeetWithAll[];
  selectedMeet: MeetWithLigue | null;
  currentMeet: MeetWithAll | null;
  currentGroup: GroupWithAll | null;
  loading: boolean;
  error: string | null;
  // Actions de base
  setMeets: (meets: MeetWithLigue[]) => void;
  setSelectedMeet: (meet: MeetWithLigue | null) => void;
  setCurrentMeet: (meet: MeetWithAll | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  // API calls existantes
  fetchMeets: (
    token?: string,
    filters?: { organisationId?: string; search?: string },
  ) => Promise<void>;
  fetchMeetWithAll: (id: string) => Promise<void>;
  getMeetWithAll: (id: string) => MeetWithAll | undefined;
  getMeet: (id: string) => MeetWithLigue | undefined;
  updateMeetWithAll: (meet: MeetWithAll, initialData?: boolean) => void;
  setMeetPublic: (
    id: string,
    isPublic: boolean,
    token: string,
  ) => Promise<boolean>;
  // Nouvelles API calls pour administration
  fetchMeet: (id: string, token: string) => Promise<void>;
  createMeet: (data: CreateMeetData, token: string) => Promise<void>;
  updateMeet: (
    id: string,
    data: UpdateMeetData,
    token: string,
  ) => Promise<void>;
  deleteMeet: (id: string, token: string) => Promise<void>;
  patchMeet: (
    patch: AthletePatch[] | GroupPatch | MeetPatch,
    weightCategories: WeightCategory[],
    ageCategories: AgeCategory[],
    disciplines: Discipline[],
    ligues: Ligue[],
  ) => Promise<void>;
  deleteAthlete: (meetId: string, athleteId: string, groupId: string) => void;
  addGroup: (meetId: string, group: GroupWithAll) => void;
  deleteGroup: (meetId: string, groupId: string) => void;
  addAthlete: (meetId: string, athlete: AthleteWithAll) => void;
  selectGroup: (meetId: string, groupId: string) => void;
  updatePowerscoutBests: (
    meetId: string,
    groupId: string,
    powerscoutBests: athletePowerscoutBests,
  ) => void;
}

const serverUrl = getWebSocketUrl();

function cloneMeetWithAllForState(meet: MeetWithAll): MeetWithAll {
  return {
    ...meet,
    records: meet.records ? [...meet.records] : meet.records,
    niveaux: meet.niveaux ? [...meet.niveaux] : meet.niveaux,
    localWidgetsURLs: meet.localWidgetsURLs
      ? [...meet.localWidgetsURLs]
      : meet.localWidgetsURLs,
    groups: meet.groups.map((group) => ({
      ...group,
      athletes: group.athletes.map((athlete) => ({ ...athlete })),
    })),
  };
}

export const useMeetStore = create<MeetState>((set, get) => ({
  patchCustomMeet: (patch) =>
    set((state) => {
      const meet = state.meetsWithAll.find((m) => m.id === patch.meetId);
      if (!meet) return state;
      const next = applyCustomDisciplineLivePatch(meet, patch);
      return {
        meetsWithAll: state.meetsWithAll.map((m) => (m === meet ? next : m)),
        currentMeet:
          state.currentMeet?.id === next.id ? next : state.currentMeet,
        currentGroup:
          state.currentMeet?.id === next.id
            ? (next.groups.find((g) => g.id === state.currentGroup?.id) ?? null)
            : state.currentGroup,
      };
    }),
  currentGroup: null,
  deleteAthlete: (meetId: string, athleteId: string, groupId: string) => {
    const meet = get().meetsWithAll.find((m) => m.id == meetId);
    if (!meet) return;
    const group = meet.groups.find((g) => g.id == groupId);
    if (!group) return;
    group.athletes = group.athletes.filter((a) => a.id != athleteId);
    get().updateMeetWithAll(meet);
  },
  addGroup: (meetId, group) => {
    const meet = get().meetsWithAll.find((m) => m.id == meetId);
    if (!meet) return;
    meet.groups = meet.groups.filter((g) => g.id !== group.id);
    meet.groups.push(group);
    get().updateMeetWithAll(meet);
  },
  deleteGroup: (meetId, groupId) => {
    const meet = get().meetsWithAll.find((m) => m.id == meetId);
    if (!meet) return;
    meet.groups = meet.groups.filter((g) => g.id != groupId);
    get().updateMeetWithAll(meet);
  },
  addAthlete: (meetId, athlete) => {
    const meet = get().meetsWithAll.find((m) => m.id == meetId);
    if (!meet) return;
    const group = meet.groups.find((g) => g.id == athlete.group_id);
    if (!group) return;
    group.athletes = group.athletes.filter((a) => a.id !== athlete.id);
    group.athletes.push(athlete);
    get().updateMeetWithAll(meet);
  },
  selectGroup: (meetId, groupId) => {
    const meet = get().meetsWithAll.find((m) => m.id == meetId);
    if (!meet) return;
    meet.groups.forEach((g) => (g.selected = g.id == groupId));
    const nextMeet = cloneMeetWithAllForState(meet);
    get().updateMeetWithAll(nextMeet);
    set({
      currentMeet: nextMeet,
      currentGroup: nextMeet.groups.find((g) => g.id == groupId) ?? null,
    });
  },
  updatePowerscoutBests: (meetId, groupId, powerscoutBests) => {
    const meet = get().meetsWithAll.find((m) => m.id == meetId);
    if (!meet) return;
    const group = meet.groups.find((g) => g.id == groupId);
    if (!group) return;
    const athlete = group.athletes.find(
      (a) => a.id == powerscoutBests.athlete_id,
    );
    if (!athlete) return;
    Object.assign(athlete, { powerscoutBests });
    get().updateMeetWithAll(meet);
  },
  meets: [],
  currentMeet: null,
  meetsWithAll: [],
  selectedMeet: null,
  async patchMeet(
    patch: AthletePatch[] | GroupPatch | MeetPatch,
    weightCategories: WeightCategory[],
    ageCategories: AgeCategory[],
    disciplines: Discipline[],
    ligues: Ligue[],
  ) {
    let connectionId: number;
    if ("length" in patch) {
      const storedMeet = get().meetsWithAll.find(
        (m) => m.id == patch[0].meetId,
      );
      if (storedMeet) {
        for (const p of patch) {
          const targetGroupId = p.data.group?.connect?.id;
          if (targetGroupId) {
            const athlete = storedMeet.groups
              .flatMap((g) => g.athletes)
              .find((a) => a.id === p.id);

            if (athlete) {
              const currentGroup = storedMeet.groups.find(
                (g) => g.id === athlete?.group_id,
              );
              const newGroup = storedMeet.groups.find(
                (g) => g.id === targetGroupId,
              );
              if (currentGroup && newGroup) {
                currentGroup.athletes = currentGroup.athletes.filter(
                  (a) => a.id !== p.id,
                );

                athlete.group_id = targetGroupId;

                if (!newGroup.athletes.some((a) => a.id === p.id)) {
                  newGroup.athletes = [...newGroup.athletes, athlete];
                }
              }
            }
          }
        }
      }
      await Promise.all(
        patch.map((pat) => {
          //AthletePatch
          const meet = get().meetsWithAll.find((m) => m.id == pat.meetId);
          if (!meet) return;
          const group = meet?.groups.find((g) => g.id === pat.groupId);
          if (!group) return;
          const athlete = group.athletes.find((a) => a.id === pat.id);
          if (!athlete) return;
          if ("weightCategory" in pat.data) {
            connectionId = pat.data.weightCategory?.connect?.id as number;
            Object.assign(athlete, {
              weightCategory: resolveLiveReference(
                connectionId,
                athlete.weightCategory,
                meet.groups.flatMap((g) =>
                  g.athletes.map((a) => a.weightCategory),
                ),
                weightCategories,
              ),
              weightCategoryId: connectionId,
            });
            delete pat.data.weightCategory;
          }
          if ("ageCategory" in pat.data) {
            connectionId = pat.data.ageCategory?.connect?.id as number;
            Object.assign(athlete, {
              ageCategory: resolveLiveReference(
                connectionId,
                athlete.ageCategory,
                meet.groups.flatMap((g) =>
                  g.athletes.map((a) => a.ageCategory),
                ),
                ageCategories,
              ),
              ageCategoryId: connectionId,
            });
            delete pat.data.ageCategory;
          }
          if ("discipline" in pat.data) {
            connectionId = pat.data.discipline?.connect?.id as number;
            Object.assign(athlete, {
              discipline: resolveLiveReference(
                connectionId,
                athlete.discipline,
                meet.groups.flatMap((g) => g.athletes.map((a) => a.discipline)),
                disciplines,
              ),
              disciplineId: connectionId,
            });
            delete pat.data.discipline;
          }

          Object.assign(athlete, pat.data);
          get().updateMeetWithAll(meet);
        }),
      );
    } else if ("meetId" in patch) {
      //GroupPatch
      const meet = get().meetsWithAll.find((m) => m.id == patch.meetId);
      if (!meet) return;
      const group = meet?.groups.find((g) => g.id === patch.id);

      if (!group) return;

      Object.assign(group, patch.data);
      get().updateMeetWithAll(meet);
    } else {
      //MeetPatch
      const meet = get().meetsWithAll.find((m) => m.id == patch.id);
      if (!meet) return;
      if ("ligue" in patch.data) {
        connectionId = patch.data.ligue?.connect?.id as number;
        Object.assign(meet, {
          ligue: ligues.find((l) => l.id === connectionId),
          ligueId: connectionId,
        });
        delete patch.data.ligue;
      }
      Object.assign(meet, patch.data);
      if (typeof patch.data.isNational === "boolean")
        recalculateMeetRankings(meet);
      get().updateMeetWithAll(meet);
    }
  },
  loading: false,
  error: null,
  // Setters
  setMeets: (meets) => set({ meets }),
  setSelectedMeet: (selectedMeet) => {
    set({ selectedMeet });
  },
  setCurrentMeet: (meet) => set({ currentMeet: meet }),
  setLoading: (loading) => set({ loading }),
  setError: (error) => set({ error }),
  // Fonctions existantes
  getMeetWithAll: (id) => get().meetsWithAll.find((m) => m.id == id),
  getMeet: (id) => get().meets.find((m) => m.id == id),
  fetchMeetWithAll: async (id) => {
    const baseline = get().meetsWithAll.find((m) => m.id === id);
    const response = await fetch(`${serverUrl}/meets/${id}`);
    if (!response.ok) return;
    const meet: MeetWithAll = await response.json();
    // A live snapshot/patch received during HTTP loading is newer than this response.
    set((state) =>
      state.meetsWithAll.find((m) => m.id === id) !== baseline
        ? state
        : {
            meetsWithAll: [
              ...state.meetsWithAll.filter((m) => m.id !== id),
              meet,
            ],
          },
    );
  },
  setMeetPublic: async (id, isPublic, token) => {
    void token;
    try {
      await apiClient.post<void>("/meets/set-is-public", { id, isPublic });
      // Mise à jour de l'état local immédiatement
      set((state) => ({
        meets: state.meets.map((meet) =>
          meet.id === id ? { ...meet, isPublic: isPublic } : meet,
        ),
      }));
      return true;
    } catch {
      return false;
    }
  },
  updateMeetWithAll: (meet, initialData = false) => {
    const nextMeet = cloneMeetWithAllForState(meet);
    set((state) => {
      const exists = state.meetsWithAll.some((m) => m.id === nextMeet.id);
      const currentGroupId = state.currentGroup?.id;
      return {
        meetsWithAll: exists
          ? state.meetsWithAll.map((m) => (m.id === nextMeet.id ? nextMeet : m))
          : [...state.meetsWithAll, nextMeet],
        currentMeet:
          state.currentMeet?.id === nextMeet.id ? nextMeet : state.currentMeet,
        currentGroup:
          state.currentMeet?.id === nextMeet.id
            ? (nextMeet.groups.find((group) => group.id === currentGroupId) ??
              nextMeet.groups.find((group) => group.selected) ??
              null)
            : state.currentGroup,
      };
    });
    if (initialData) {
      set({
        currentMeet: nextMeet,
        currentGroup: nextMeet.groups.find((g) => g.selected) ?? null,
      });
    }
  },
  fetchMeets: async (token, filters) => {
    set({ loading: true, error: null });
    try {
      void token;
      // OptionalJwtAuthGuard: anonymous sees public meets; admin JWT sees all
      const params = new URLSearchParams();
      if (filters?.organisationId) {
        params.set("organisationId", filters.organisationId);
      }
      if (filters?.search) params.set("search", filters.search);
      const query = params.size ? `?${params.toString()}` : "";
      const meets = await apiClient.get<MeetWithLigue[]>(`/meets/all${query}`, {
        requiresAuth: "optional",
      });
      set({ meets: meets, loading: false });
    } catch {
      set({ error: "Erreur de connexion au serveur", loading: false });
    }
  },
  // Récupérer un meet par ID pour administration
  fetchMeet: async (id, token) => {
    set({ loading: true, error: null });
    try {
      void token;
      // GET /meets/:id is public (no JWT guard)
      const meet = await apiClient.get<MeetWithLigue>(`/meets/${id}`, {
        requiresAuth: false,
      });
      set({ selectedMeet: meet, loading: false });
    } catch {
      set({ error: "Erreur de connexion au serveur", loading: false });
    }
  },
  // Créer un nouveau meet
  createMeet: async (data, token) => {
    set({ loading: true, error: null });
    try {
      void token;
      const newMeet = await apiClient.post<MeetWithLigue>("/meets", data);
      set((state) => ({
        meets: [newMeet, ...state.meets],
        loading: false,
      }));
    } catch (error) {
      set({ error: "Erreur de connexion au serveur", loading: false });
      throw error;
    }
  },
  // Mettre à jour un meet
  updateMeet: async (id, data, token) => {
    set({ loading: true, error: null });
    try {
      void token;
      const updatedMeet = await apiClient.patch<MeetWithLigue>(
        `/meets/${id}`,
        data,
      );
      set((state) => ({
        meets: state.meets.map((meet) => (meet.id === id ? updatedMeet : meet)),
        selectedMeet:
          state.selectedMeet?.id === id ? updatedMeet : state.selectedMeet,
        loading: false,
      }));
    } catch (error) {
      set({ error: "Erreur de connexion au serveur", loading: false });
      throw error;
    }
  },
  // Supprimer un meet
  deleteMeet: async (id, token) => {
    set({ loading: true, error: null });
    try {
      void token;
      await apiClient.delete<void>(`/meets/${id}`);
      set((state) => ({
        meets: state.meets.filter((meet) => meet.id !== id),
        selectedMeet: state.selectedMeet?.id === id ? null : state.selectedMeet,
        loading: false,
      }));
    } catch (error) {
      set({ error: "Erreur de connexion au serveur", loading: false });
      throw error;
    }
  },
}));
